// Mock data for Waan Travel - Singapore Visa Online

export const COMPANY = {
  brand: 'Visasg.com',
  name: 'Visasg.com',
  legalName: 'Waan Travel Pte. Ltd.',
  short: 'Visasg.com',
  address: '810B Choa Chu Kang Ave 7, #14-527, Singapore 682810',
  phone: '+65 6519 5014',
  whatsapp: '+65 8959 2280',
  whatsappRaw: '6589592280',
  email: 'admin@waantravel.com',
  license: 'TA03795',
  since: 2018,
};

// VisaSG's public customer-facing note. VisaSG does not submit visa
// applications to ICA. Retained as an informational note used by legacy
// components until they are re-linked to informational surfaces only.
export const PROCESSING_NOTICE = "Singapore visa processing times are determined by the Immigration & Checkpoints Authority (ICA) and are not guaranteed. Each application is assessed by ICA on its individual merits.";

export const PROCESSING_SHORT = "Singapore visa processing is determined by ICA.";

// Non-numeric service facts. All commercial visa-service claims removed.
// VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an
// authorised Singapore visa agent; the following statistics only reflect
// factual company attributes and legitimate travel services.
export const STATS = [
  { label: 'Singapore-licensed travel agent', value: 'STB TA03795', sub: 'WAAN TRAVEL PTE. LTD.' },
  { label: 'Legitimate travel services', value: 'Hotel, Flight, Tour', sub: 'Bookings, itineraries and transportation arrangements.' },
  { label: 'ICA prescribed visa fee', value: 'S$30', sub: 'Per application. Payable to ICA through the appropriate channel prescribed by ICA.' },
  { label: 'Human WhatsApp support', value: 'Real people', sub: 'Answered by a Singapore-based travel consultant.' },
];

// Nationalities we serve. `applicationEnabled` controls whether the country
// can be selected in application flows. Disabled countries keep their SEO
// landing page (visible + indexable) but are hidden from all selectors,
// so we can flip them back on without touching code or losing SEO.
export const NATIONALITIES = [
  // ---- South Asia ----
  { code: 'in', name: 'India', priceFrom: 79, popular: true, applicationEnabled: true },
  { code: 'pk', name: 'Pakistan', priceFrom: 119, applicationEnabled: true },
  // ---- East Asia ----
  { code: 'cn', name: 'China', priceFrom: 89, popular: true, applicationEnabled: false },
  { code: 'kp', name: "Democratic People's Republic of Korea", priceFrom: 119, applicationEnabled: false },
  // ---- South Asia (currently unsupported, SEO retained) ----
  { code: 'bd', name: 'Bangladesh', priceFrom: 89, applicationEnabled: false },
  { code: 'lk', name: 'Sri Lanka', priceFrom: 89, applicationEnabled: false },
  { code: 'np', name: 'Nepal', priceFrom: 89, applicationEnabled: false },
  // ---- Middle East & North Africa ----
  { code: 'af', name: 'Afghanistan', priceFrom: 119, applicationEnabled: true },
  { code: 'eg', name: 'Egypt', priceFrom: 119, applicationEnabled: true },
  { code: 'dz', name: 'Algeria', priceFrom: 119, applicationEnabled: true },
  { code: 'iq', name: 'Iraq', priceFrom: 119, applicationEnabled: false },
  { code: 'ir', name: 'Iran', priceFrom: 119, applicationEnabled: false },
  { code: 'sy', name: 'Syria', priceFrom: 119, applicationEnabled: false },
  { code: 'ye', name: 'Yemen', priceFrom: 119, applicationEnabled: true },
  { code: 'jo', name: 'Jordan', priceFrom: 119, applicationEnabled: true },
  { code: 'lb', name: 'Lebanon', priceFrom: 119, applicationEnabled: false },
  { code: 'ma', name: 'Morocco', priceFrom: 119, applicationEnabled: true },
  { code: 'tn', name: 'Tunisia', priceFrom: 119, applicationEnabled: true },
  { code: 'ly', name: 'Libya', priceFrom: 119, applicationEnabled: false },
  { code: 'sd', name: 'Sudan', priceFrom: 119, applicationEnabled: true },
  { code: 'ss', name: 'South Sudan', priceFrom: 119, applicationEnabled: true },
  // ---- Africa ----
  { code: 'ml', name: 'Mali', priceFrom: 119, applicationEnabled: true },
  { code: 'ng', name: 'Nigeria', priceFrom: 119, applicationEnabled: true },
  { code: 'so', name: 'Somalia', priceFrom: 119, applicationEnabled: true },
  // ---- Central Asia & CIS ----
  { code: 'kz', name: 'Kazakhstan', priceFrom: 95, applicationEnabled: true },
  { code: 'uz', name: 'Uzbekistan', priceFrom: 95, applicationEnabled: true },
  { code: 'kg', name: 'Kyrgyzstan', priceFrom: 95, applicationEnabled: true },
  { code: 'tj', name: 'Tajikistan', priceFrom: 95, applicationEnabled: true },
  { code: 'tm', name: 'Turkmenistan', priceFrom: 95, applicationEnabled: true },
  { code: 'az', name: 'Azerbaijan', priceFrom: 95, applicationEnabled: true },
  { code: 'am', name: 'Armenia', priceFrom: 95, applicationEnabled: true },
  { code: 'ge', name: 'Georgia', priceFrom: 95, applicationEnabled: true },
  { code: 'by', name: 'Belarus', priceFrom: 95, applicationEnabled: false },
  { code: 'md', name: 'Moldova', priceFrom: 95, applicationEnabled: true },
  { code: 'ua', name: 'Ukraine', priceFrom: 95, applicationEnabled: true },
  { code: 'ru', name: 'Russia', priceFrom: 95, applicationEnabled: false },
  // ---- Europe (Balkans) ----
  { code: 'xk', name: 'Kosovo', priceFrom: 95, applicationEnabled: true },
];

// Helper: returns only countries where applications are currently accepted.
// Use this in every UI selector (Apply form, Eligibility, dropdowns).
// SEO pages should still iterate the full NATIONALITIES list.
export function enabledNationalities(list = NATIONALITIES) {
  return list.filter((n) => n && n.applicationEnabled !== false);
}
export function isNationalityEnabled(code, list = NATIONALITIES) {
  const n = list.find((x) => x && x.code === code);
  return !!n && n.applicationEnabled !== false;
}

// Region grouping for the Requirements & Fees by Country page
export const REGIONS = [
  {
    id: 'south-asia',
    name: 'South Asia',
    desc: 'Singapore visa information for South Asian passport holders.',
    codes: ['in', 'bd', 'lk', 'np', 'pk', 'af'],
  },
  {
    id: 'east-asia',
    name: 'East Asia',
    desc: 'Singapore visa information for eligible East Asian passports.',
    codes: ['cn', 'kp'],
  },
  {
    id: 'central-asia',
    name: 'Central Asia & CIS',
    desc: 'Singapore visa information for Central Asia and CIS passport holders.',
    codes: ['kz', 'uz', 'kg', 'tj', 'tm', 'az', 'am', 'ge', 'by', 'md', 'ua', 'ru'],
  },
  {
    id: 'mena',
    name: 'Middle East & North Africa',
    desc: 'Singapore visa information for MENA passport holders.',
    codes: ['eg', 'dz', 'iq', 'ir', 'sy', 'ye', 'jo', 'lb', 'ma', 'tn', 'ly', 'sd', 'ss'],
  },
  {
    id: 'africa',
    name: 'Africa',
    desc: 'Singapore visa information for African passport holders.',
    codes: ['ml', 'ng', 'so'],
  },
  {
    id: 'balkans',
    name: 'Balkans',
    desc: 'Singapore visa information for Balkan passport holders.',
    codes: ['xk'],
  },
];

// Common document requirements shown on every country detail page.
// Only the documents ICA itself lists on its country pages are shown.
// VisaSG does not submit Singapore visa applications on behalf of
// customers; no "collected by VisaSG" intake documents are advertised.
export const COMMON_REQUIREMENTS = [
  { title: 'Passport bio-data page', desc: 'Passport valid for at least six months from your date of entry into Singapore (per ICA).', group: 'ica' },
  { title: 'Recent passport-style photo', desc: 'Colour, taken within the last three months, per ICA photo guidelines.', group: 'ica' },
];

// Per-country overrides.
//
// LOI status is the source of truth in SEO_COUNTRIES (seo-content.js),
// verified against official ICA country pages on 2026-02-13. Historic
// unverified `loi`, `stayMax`, and `processing` overrides that
// contradicted ICA have been removed. VisaSG-specific intake extras
// stay here but are clearly labelled to end-users as "requested by
// VisaSG, not required by ICA" via the country-page renderer.
//
// If VisaSG later operationally verifies a specific intake requirement
// per nationality (e.g. Aadhaar / PAN copy for India), it can be
// restored as an entry with a clear VisaSG-side note.
export const COUNTRY_DETAILS = {
  // (Empty on purpose.) Prior overrides for in, cn, pk, bd, lk, np, eg,
  // ru, ua, kz, uz were dropped because their loi / stayMax / processing
  // fields either contradicted ICA or promised specific documents that
  // could not be operationally confirmed in review. Add a new entry ONLY
  // once the VisaSG operations team has confirmed the requirement.
};

export const VISA_TYPES = [
  {
    id: 'single',
    name: 'Single Entry',
    priceAdd: 0,
    desc: 'One entry into Singapore. Period of stay is determined by the electronic Visit Pass (e-Pass) issued at the checkpoint by ICA. The requested visa type is subject to ICA assessment and issuance.',
  },
  {
    id: 'double',
    name: 'Double Entry',
    priceAdd: 40,
    desc: 'Two entries into Singapore. Period of stay per entry is determined by the electronic Visit Pass (e-Pass) issued at the checkpoint by ICA. The requested visa type is subject to ICA assessment and issuance.',
  },
];

export const PROCESSING_SPEEDS = [
  {
    id: 'standard',
    name: 'Standard Processing',
    days: '3 to 5 working days*',
    priceAdd: 0,
    badge: 'Only option',
    notice: PROCESSING_NOTICE,
  },
];

export const ADDONS = [];

export const FAQS = [
  {
    group: 'About VisaSG / Waan Travel',
    items: [
      { q: 'Are you the Singapore government or ICA?', a: 'No. VisaSG is operated by WAAN TRAVEL PTE. LTD., a Singapore-licensed travel agency under Singapore Tourism Board (STB) Travel Agent Licence No. TA03795. VisaSG/Waan Travel Pte. Ltd. is not an ICA Strategic Partner or an authorised Singapore visa agent and does not submit Singapore visa applications on behalf of customers. The official Singapore Government portal is ica.gov.sg.' },
      { q: 'Can you submit my Singapore visa application to ICA?', a: 'No. Travellers requiring a Singapore visa should apply through the appropriate channel prescribed by the Immigration & Checkpoints Authority (ICA). Visa applications are assessed by ICA on their individual merits.' },
      { q: 'What is the Singapore visa processing fee?', a: 'The prescribed ICA visa processing fee is S$30 per application. This fee is payable to ICA through the appropriate channel prescribed by ICA and is not payable to Waan Travel Pte. Ltd.' },
    ],
  },
  {
    group: 'Travel services by Waan Travel',
    items: [
      { q: 'What travel services do you offer?', a: 'As a Singapore-licensed travel agency, Waan Travel Pte. Ltd. arranges hotel bookings, flight tickets, tour packages, transportation arrangements and travel itinerary planning for visitors to Singapore. These services are separate from any Singapore visa submission.' },
      { q: 'How can I get a quote for a travel service?', a: 'Message us on WhatsApp with your travel dates and requirements — our Singapore-based team will reply during business hours.' },
    ],
  },
];

export const STEPS = [
  {
    n: 1,
    title: 'Check visa requirements',
    desc: 'Use our informational country pages to review the Singapore visa requirements published by ICA for your nationality.',
  },
  {
    n: 2,
    title: 'Apply through the official ICA channel',
    desc: 'Visa applications are submitted through the appropriate channel prescribed by ICA. The prescribed ICA visa processing fee is S$30 per application.',
  },
  {
    n: 3,
    title: 'Arrange your Singapore trip with us',
    desc: 'Waan Travel Pte. Ltd. is a Singapore-licensed travel agency. We can arrange hotel bookings, flight tickets, tour packages, transportation and itinerary planning for your trip.',
  },
];

export const WHY = [
  {
    title: 'Singapore-licensed travel agency',
    desc: 'WAAN TRAVEL PTE. LTD. is a Singapore-licensed travel agency under Singapore Tourism Board (STB) Travel Agent Licence No. TA03795.',
    icon: 'ShieldCheck',
    highlight: true,
  },
  {
    title: 'Human WhatsApp support',
    desc: 'A real Singapore-based travel consultant answers on WhatsApp in English & Hindi during business hours.',
    icon: 'MessagesSquare',
  },
  {
    title: 'Legitimate travel services',
    desc: 'Hotel booking, flight ticket booking, tour packages, transportation arrangements and travel itinerary planning.',
    icon: 'FileCheck2',
  },
  {
    title: 'Transparent travel-service pricing',
    desc: 'All travel-service fees are quoted in advance. No commercial visa-agent fees. The prescribed ICA visa processing fee is S$30 per application, payable to ICA through the appropriate channel.',
    icon: 'Lock',
  },
];

export const COMPARISON = [
  { row: 'Singapore visa applications', ica: 'Assessed and issued solely by ICA', us: 'Waan Travel does NOT submit Singapore visa applications on behalf of customers' },
  { row: 'ICA prescribed visa processing fee', ica: 'S$30 per application', us: 'Payable to ICA through the appropriate channel prescribed by ICA' },
  { row: 'Hotel booking', ica: 'Not provided', us: 'Arranged by Waan Travel as a Singapore-licensed travel agency' },
  { row: 'Flight ticket booking', ica: 'Not provided', us: 'Arranged by Waan Travel as a Singapore-licensed travel agency' },
  { row: 'Tour packages & itineraries', ica: 'Not provided', us: 'Free & Easy tour packages, sightseeing itineraries' },
  { row: 'Transportation arrangements', ica: 'Not provided', us: 'Airport transfers and in-city transportation on request' },
  { row: 'Travel consultation', ica: 'Not provided', us: 'WhatsApp support in English & Hindi during business hours' },
];

// Testimonials removed. Historical visa-service testimonials were tied to
// language and outcomes that ICA has instructed Waan Travel Pte. Ltd. to
// cease representing. No testimonials are shown until suitable travel-
// services testimonials are collected and approved.
export const TESTIMONIALS = [];

export const FAQS_LEGACY = [];

export const IMAGES = {
  heroSkyline: 'https://images.unsplash.com/photo-1525625293386-3f8f99389edd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  marinaBayNight: 'https://images.unsplash.com/photo-1496939376851-89342e90adcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600',
  merlion: 'https://images.unsplash.com/photo-1600664356348-10686526af4f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  gardens: 'https://images.unsplash.com/photo-1565967511849-76a60a516170?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  passport: 'https://images.unsplash.com/photo-1454496406107-dc34337da8d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  documents: 'https://images.unsplash.com/photo-1581553673739-c4906b5d0de8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  airport1: 'https://images.unsplash.com/photo-1504150558240-0b4fd8946624?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  airport2: 'https://images.unsplash.com/photo-1549897411-b06572cdf806?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200',
  sponsor: 'https://images.pexels.com/photos/16562841/pexels-photo-16562841.jpeg?auto=compress&cs=tinysrgb&w=1200',
};
